import { DropdownButton, DropdownButtonItem } from '@salecycle/ui-component-library';
import * as RestApi from 'core/network/RestApi';
import { RestApiError } from 'core/network/RestApi';
import useNotifications from 'core/notifications/useNotifications';
import { State as LifeCycleState } from 'entities/lifecycle';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { generatePath } from 'react-router';
import { useHistory, useParams } from 'react-router-dom';
import { OffsiteCampaignEndpoints } from 'sections/offsite-campaigns/constants/api-endpoints';
import { OffsiteCampaignRoute } from 'sections/offsite-campaigns/routes/offsite-campaign-routes';
import { IOffsiteCampaign } from '../../entities/offsiteCampaign';
import { useOffsiteCampaignsListDispatch } from '../context/UseOffsiteCampaignsListContext';
import Site from 'sections/client-management/entities/site';

interface IOwnProps {
  campaign: IOffsiteCampaign;
}

export type IProps = IOwnProps;

export const CampaignsListItemActionsButton: FC<IProps> = (props: IProps) => {
  const { t } = useTranslation('campaigns');
  const { push } = useHistory();
  const { siteId } = useParams<{ siteId: string }>();
  const dispatch = useOffsiteCampaignsListDispatch();
  const { showNotification } = useNotifications();

  const putCampaignMutation = useMutation((campaign: IOffsiteCampaign) =>
    RestApi.put(generatePath(OffsiteCampaignEndpoints.OFFSITE_PUT, { siteId, campaignId: campaign.id }), campaign),
  );

  const handleUpdateCampaignState = async (state: LifeCycleState) => {
    if (state === 'ACTIVE') {
      const siteResult = await RestApi.get<Site>(generatePath(OffsiteCampaignEndpoints.GET_SITE, { siteId }));
      if (!siteResult.data.unsubscribeSettings) {
        showNotification(`Unable to set campaign live due to missing unsubscribe settings`, 'error');
        return;
      }
    }

    const result = await RestApi.get<IOffsiteCampaign>(
      generatePath(OffsiteCampaignEndpoints.OFFSITE_GET_BY_ID, { siteId, campaignId: props.campaign.id }),
    );
    result.data.state = state;
    putCampaignMutation.mutate(result.data, {
      onSuccess: data => {
        showNotification(`Campaign updated`, 'normal');
        dispatch({
          type: 'UPDATE_CAMPAIGN',
          payload: { campaign: result.data },
        });
      },
      onError: (error: RestApiError, variables, context) => {
        showNotification(`Unable to Update Campaign`, 'error');
      },
    });
  };

  const openCloneModal = () => {
    dispatch({
      type: 'OPEN_CLONE_MODAL',
      payload: { campaign: props.campaign },
    });
  };

  const editClick = () =>
    push(generatePath(OffsiteCampaignRoute.OFFSITE_EDIT, { siteId, campaignId: props.campaign.id }));
  const onClone = () => openCloneModal();
  const onStart = () => handleUpdateCampaignState('ACTIVE');
  const onPause = () => handleUpdateCampaignState('INACTIVE');
  const onDelete = () => handleUpdateCampaignState('DELETED');
  const onTest = () => handleUpdateCampaignState('TESTING');
  const onMigration = () => handleUpdateCampaignState('MIGRATION');

  return (
    <>
      <DropdownButton variation="clean" icon="ellipsis-v" menuPlacement="right">
        {props.campaign.state === 'ACTIVE' ? (
          <DropdownButtonItem onClick={onPause}>{t('campaigns:campaignActions.pause')}</DropdownButtonItem>
        ) : null}
        {props.campaign.state === 'INACTIVE' ? (
          <DropdownButtonItem onClick={onStart}>{t('campaigns:campaignActions.start')}</DropdownButtonItem>
        ) : null}
        {props.campaign.state !== 'DELETED' ? (
          <DropdownButtonItem onClick={onDelete}>{t('campaigns:campaignActions.archive')}</DropdownButtonItem>
        ) : (
          <DropdownButtonItem onClick={onPause}>{t('campaigns:campaignActions.restore')}</DropdownButtonItem>
        )}
        {props.campaign.state === 'INACTIVE' || props.campaign.state === 'ACTIVE' ? (
          <>
            <DropdownButtonItem onClick={onTest}>{t('campaigns:campaignActions.test')}</DropdownButtonItem>
          </>
        ) : null}
        {props.campaign.state === 'MIGRATION' ? (
          <>
            <DropdownButtonItem onClick={onTest}>{t('campaigns:campaignActions.test')}</DropdownButtonItem>
            <DropdownButtonItem onClick={onStart}>{t('campaigns:campaignActions.start')}</DropdownButtonItem>
          </>
        ) : null}
        {props.campaign.state === 'TESTING' ? (
          <>
            <DropdownButtonItem onClick={onStart}>{t('campaigns:campaignActions.start')}</DropdownButtonItem>
          </>
        ) : null}

        {props.campaign.state === 'INACTIVE' ||
        props.campaign.state === 'ACTIVE' ||
        props.campaign.state === 'TESTING' ? (
          <DropdownButtonItem onClick={onMigration}>{t('campaigns:campaignActions.migrate')}</DropdownButtonItem>
        ) : null}

        <DropdownButtonItem onClick={editClick}>{t('campaigns:campaignActions.edit')}</DropdownButtonItem>
        <DropdownButtonItem onClick={onClone}>{t('campaigns:campaignActions.clone')}</DropdownButtonItem>
      </DropdownButton>
    </>
  );
};

export default CampaignsListItemActionsButton;
